// Free, public NHTSA endpoints (no key, CORS-enabled). Everything the site shows about a
// vehicle comes from here — nothing is invented or cached on our side.
const VPIC = 'https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues';
const RECALLS = 'https://api.nhtsa.gov/recalls/recallsByVehicle';
const COMPLAINTS = 'https://api.nhtsa.gov/complaints/complaintsByVehicle';
const RATINGS = 'https://api.nhtsa.gov/SafetyRatings';

export const isVin = (v) => /^[A-HJ-NPR-Z0-9]{17}$/.test(v);

export function vinError(v) {
  if (v.length !== 17) return `A VIN has 17 characters — you entered ${v.length}.`;
  if (/[IOQ]/.test(v)) return 'VINs never contain the letters I, O or Q. Check for look-alike characters (0/O, 1/I).';
  return 'That does not look like a valid VIN.';
}

const titleCase = (s) => (s || '').toLowerCase().replace(/(^|[\s/-])([a-z])/g, (m, a, b) => a + b.toUpperCase());

async function getJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const q = ({ make, model, year }) =>
  `make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}&modelYear=${encodeURIComponent(year)}`;

const SPEC_FIELDS = [
  ['BodyClass', 'Body'],
  ['DriveType', 'Drive'],
  ['DisplacementL', 'Engine size', (v) => `${v}L`],
  ['EngineCylinders', 'Cylinders'],
  ['EngineHP', 'Horsepower'],
  ['FuelTypePrimary', 'Fuel'],
  ['TransmissionStyle', 'Transmission'],
  ['Doors', 'Doors'],
  ['Seats', 'Seats'],
  ['PlantCountry', 'Built in'],
  ['GVWR', 'Weight class'],
  ['ABS', 'ABS'],
  ['ESC', 'Stability control'],
  ['AirBagLocFront', 'Front airbags'],
  ['AirBagLocSide', 'Side airbags'],
  ['AirBagLocCurtain', 'Curtain airbags'],
];

function buildSpecs(d) {
  const blank = new Set(['', 'not applicable', 'n/a', 'not reported']);
  return SPEC_FIELDS.map(([key, label, format]) => {
    const raw = (d[key] || '').toString().trim();
    if (blank.has(raw.toLowerCase())) return null;
    return [label, format ? format(raw) : raw];
  }).filter(Boolean);
}

async function decodeVin(vin) {
  const json = await getJson(`${VPIC}/${vin}?format=json`);
  const d = (json.Results && json.Results[0]) || {};
  if (!d.Make || !d.ModelYear || !d.Model) {
    throw new Error("That VIN didn't decode. Double-check it — VINs never use the letters I, O or Q.");
  }
  const codes = (d.ErrorCode || '').split(',').map((c) => c.trim());
  return {
    year: d.ModelYear,
    make: d.Make,
    model: d.Model,
    trim: d.Trim,
    specs: buildSpecs(d),
    plant: [d.PlantCity, d.PlantState, d.PlantCountry].filter(Boolean).join(', '),
    // vPIC error code 1 = the 9th-character check digit does not compute → very likely a typo.
    checkDigitWarning: codes.includes('1'),
  };
}

// NHTSA dates on the recalls endpoint come as DD/MM/YYYY.
function recallDate(s) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s || '');
  if (!m) return { label: s || '', sort: 0 };
  const d = new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
  return { label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }), sort: d.getTime() };
}

async function fetchRecalls(v) {
  const json = await getJson(`${RECALLS}?${q(v)}`);
  const seen = new Set();
  return (json.results || [])
    .filter((r) => !seen.has(r.NHTSACampaignNumber) && seen.add(r.NHTSACampaignNumber))
    .map((r) => {
      const date = recallDate(r.ReportReceivedDate);
      return {
        id: r.NHTSACampaignNumber,
        date: date.label,
        sort: date.sort,
        component: titleCase((r.Component || 'Safety recall').split(':')[0]),
        summary: r.Summary || '',
        consequence: r.Consequence || '',
        remedy: r.Remedy || '',
        doNotDrive: !!(r.parkIt || r.parkOutSide),
      };
    })
    .sort((a, b) => b.sort - a.sort);
}

async function fetchComplaints(v) {
  const json = await getJson(`${COMPLAINTS}?${q(v)}`);
  const rows = json.results || [];
  const byComponent = {};
  let crash = 0, fire = 0, injuries = 0, deaths = 0;
  rows.forEach((r) => {
    if (r.crash) crash += 1;
    if (r.fire) fire += 1;
    injuries += r.numberOfInjuries || 0;
    deaths += r.numberOfDeaths || 0;
    (r.components || '').split(',').map((c) => c.trim()).filter(Boolean).forEach((c) => {
      const name = titleCase(c.split(':')[0]);
      byComponent[name] = (byComponent[name] || 0) + 1;
    });
  });
  const top = Object.entries(byComponent).sort((a, b) => b[1] - a[1]).slice(0, 5);
  return { count: json.count ?? rows.length, crash, fire, injuries, deaths, top };
}

const star = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

async function fetchSafety(v) {
  const list = await getJson(`${RATINGS}/modelyear/${encodeURIComponent(v.year)}/make/${encodeURIComponent(v.make)}/model/${encodeURIComponent(v.model)}`);
  const variants = (list.Results || []).slice(0, 4);
  for (const variant of variants) {
    const r = ((await getJson(`${RATINGS}/VehicleId/${variant.VehicleId}`)).Results || [])[0];
    if (r && star(r.OverallRating) !== null) {
      return {
        overall: star(r.OverallRating),
        front: star(r.OverallFrontCrashRating),
        side: star(r.OverallSideCrashRating),
        rollover: star(r.RolloverRating),
        description: variant.VehicleDescription,
      };
    }
  }
  return null;
}

// input: { vin } or { year, make, model }
export async function loadVehicle(input) {
  let base;
  let extra = {};
  if (input.vin) {
    const d = await decodeVin(input.vin);
    base = { year: d.year, make: titleCase(d.make), model: d.model };
    extra = { vin: input.vin, trim: d.trim, specs: d.specs, plant: d.plant, checkDigitWarning: d.checkDigitWarning };
  } else {
    base = { year: String(input.year).trim(), make: titleCase(input.make.trim()), model: input.model.trim() };
  }

  const [recalls, complaints, safety] = await Promise.all([
    // NHTSA answers 400 for a make/model it doesn't know — treat that as "no results", not an outage.
    fetchRecalls(base).catch((e) => { if (/HTTP 400/.test(e.message)) return []; throw e; }),
    fetchComplaints(base).catch(() => null),
    fetchSafety(base).catch(() => null),
  ]);

  if (!input.vin && recalls.length === 0 && (!complaints || complaints.count === 0) && !safety) {
    throw new Error('We found nothing for that year, make and model. Check the spelling (for example "Honda" / "Civic") and the year.');
  }

  return {
    title: `${base.year} ${base.make} ${base.model}`,
    ...base,
    ...extra,
    recalls,
    complaints,
    safety,
  };
}
